import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet } from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState } from '@share/utils';

import { useAccountBalances } from '../../balances';
import { getHubThemeStyle } from '../../utils';

import styles from './styles.module.scss';

interface IProps {
  onClick: () => void;
  className?: string;
}

interface ISegment {
  key: string;
  value: string;
  label: string;
  className: string;
}

/**
 * The header's balance pill for new My Account tenants: one segment per balance, in the same colours
 * as the Overview cards (agent commissions navy, money wallet tenant blue, leisure credits green).
 * Opens the account menu, like the chip it replaces.
 */
export const AccountBalanceChip = ({ onClick, className }: IProps): React.ReactElement | null => {
  const intl = useIntl();
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const { showWallet, wallet, formatWallet, points, pointsLabel, showCommissions, commissions, formatUsd } = useAccountBalances();

  const segments: ISegment[] = [];
  // The chip shows money the member holds. Commissions earned in a period is a report figure, so it
  // only stands in when the agent has no wallet to show.
  if (showCommissions && commissions && !showWallet) {
    segments.push({
      key: 'commissions',
      value: formatUsd(commissions.commissions),
      label: intl.formatMessage({ id: 'my.account.hub.section.reports' }),
      className: styles.chipCommissions,
    });
  }
  if (showWallet) {
    segments.push({
      key: 'wallet',
      value: formatWallet(wallet?.balance),
      label: intl.formatMessage({ id: 'my.account.hub.wallet.title' }),
      className: styles.chipWallet,
    });
  }
  if (points !== null) {
    segments.push({ key: 'points', value: intl.formatNumber(points), label: pointsLabel, className: styles.chipPoints });
  }

  if (!segments.length) {
    return null;
  }

  const description = segments.map(segment => `${segment.label} ${segment.value}`).join(', ');

  return (
    <button
      type="button"
      className={`${styles.chip} ${className || ''}`}
      style={getHubThemeStyle(account)}
      onClick={onClick}
      aria-label={intl.formatMessage({ id: 'my.account.hub.menu.chip_label' }, { balances: description })}
    >
      {segments.map((segment, index) => (
        <span key={segment.key} className={`${styles.chipSegment} ${segment.className}`}>
          {index === 0 ? <FontAwesomeIcon icon={faWallet} className={styles.chipIcon} aria-hidden="true" /> : null}
          {segment.value}
        </span>
      ))}
    </button>
  );
};
